import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  Squares2X2Icon,
  MapPinIcon,
  SparklesIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { brandsService } from '../services/firebase';
import StorePageHero from '../components/layout/StorePageHero';

const TRUST_FEATURES = [
  {
    icon: ShieldCheckIcon,
    title: '35+ Years',
    description: 'Of industry experience',
    color: 'bg-primary-100 text-primary-600',
  },
  {
    icon: Squares2X2Icon,
    title: 'Wide Range',
    description: 'Brand & product options',
    color: 'bg-violet-100 text-violet-600',
  },
  {
    icon: MapPinIcon,
    title: 'Pan India',
    description: 'End-to-end delivery',
    color: 'bg-accent-100 text-accent-600',
  },
  {
    icon: SparklesIcon,
    title: 'Branding',
    description: 'Custom solutions for your brand',
    color: 'bg-emerald-100 text-emerald-600',
  },
];

const PRICE_RANGES = [
  { min: 0, max: 500, label: 'Below ₹500', hint: 'Starter gifts' },
  { min: 501, max: 1000, label: '₹501 – ₹1,000', hint: 'Popular picks' },
  { min: 1001, max: 1500, label: '₹1,001 – ₹1,500', hint: 'Mid-range' },
  { min: 1501, max: 2000, label: '₹1,501 – ₹2,000', hint: 'Premium feel' },
  { min: 2001, max: 2500, label: '₹2,001 – ₹2,500', hint: 'Executive gifts' },
  { min: 2501, max: 3000, label: '₹2,501 – ₹3,000', hint: 'Top tier' },
  { min: 3001, max: 5000, label: '₹3,001 – ₹5,000', hint: 'Luxury range' },
  { min: 5001, max: 10000, label: '₹5,001 – ₹10,000', hint: 'Elite selection' },
  { min: 10001, max: 999999, label: '₹10,001 & Above', hint: 'Bespoke solutions' },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.45 },
};

const Home = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const isPharma = searchParams.get('pharma') === 'true';
  const isLifestyle = searchParams.get('lifestyle') === 'true';

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const activeBrands = await brandsService.getAll(true);
        setBrands(activeBrands);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const buildProductsUrl = (params = {}) => {
    const query = new URLSearchParams(params);
    if (isPharma) query.set('pharma', 'true');
    if (isLifestyle) query.set('lifestyle', 'true');
    const qs = query.toString();
    return qs ? `/products?${qs}` : '/products';
  };

  const handleBrandClick = (brandName) => {
    navigate(buildProductsUrl({ brand: brandName }));
  };

  const handlePriceFilterClick = (minPrice, maxPrice) => {
    navigate(buildProductsUrl({ minPrice, maxPrice }));
  };

  const handleBrowseProducts = () => {
    navigate(buildProductsUrl());
  };

  const browseButtonLabel = isPharma
    ? 'Browse Pharma Products'
    : isLifestyle
      ? 'Browse Lifestyle Products'
      : 'Browse All Products';

  return (
    <div className="store-page">
      {isPharma && (
        <div className="store-pharma-banner">
          <span className="store-pharma-banner-text">Viewing Pharma Products</span>
        </div>
      )}
      {isLifestyle && (
        <div className="store-lifestyle-banner">
          <span className="store-lifestyle-banner-text">Viewing Lifestyle Products</span>
        </div>
      )}

      <StorePageHero centered>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="store-eyebrow">Welcome to Aalap</p>
          <img
            src="/Aalap.png"
            alt="Aalap Electronics"
            className="h-20 sm:h-24 w-auto mx-auto mb-6 object-contain"
          />
          <h1 className="store-title">Premium Corporate Gifts</h1>
          <p className="store-subtitle max-w-2xl mx-auto">
            From ₹199 to ₹5 lakhs — whatever your budget, we have the perfect solution for your brand.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button type="button" onClick={handleBrowseProducts} className="store-cta-primary w-full sm:w-auto">
              {browseButtonLabel}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <Link to="/shortlist" className="store-cta-secondary w-full sm:w-auto">
              View Shortlist
            </Link>
          </div>
        </motion.div>
      </StorePageHero>

      {/* Trust features */}
      <section className="store-section pt-0">
        <motion.div
          {...fadeUp}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          {TRUST_FEATURES.map(({ icon: Icon, title, description, color }) => (
            <div key={title} className="store-trust-card">
              <div className={`flex items-center justify-center w-11 h-11 rounded-xl mb-3 ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900">{title}</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">{description}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Price ranges */}
      <section className="store-section bg-white border-y border-slate-200/80">
        <motion.div {...fadeUp} className="store-section-header">
          <h2 className="store-section-title">Shop by Budget</h2>
          <p className="store-section-subtitle">
            Find products within your price range — tap a bracket to explore matching gifts.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4"
        >
          {PRICE_RANGES.map(({ min, max, label, hint }) => (
            <button
              key={label}
              type="button"
              onClick={() => handlePriceFilterClick(min, max)}
              className="store-interactive-card group"
            >
              <span className="text-sm sm:text-base font-bold text-primary-600 group-hover:text-primary-700 text-center leading-snug">
                {label}
              </span>
              <span className="mt-1.5 text-xs text-slate-500 group-hover:text-slate-600">
                {hint}
              </span>
              <ArrowRightIcon className="absolute bottom-3 right-3 h-4 w-4 text-slate-300 group-hover:text-primary-500 opacity-0 group-hover:opacity-100 transition-all duration-300" />
            </button>
          ))}
        </motion.div>
      </section>

      {/* Brands */}
      <section className="store-section">
        <motion.div {...fadeUp} className="store-section-header">
          <h2 className="store-section-title">Our Brands</h2>
          <p className="store-section-subtitle">
            Discover premium brands in our collection — select a brand to view its products.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="h-28 sm:h-32 rounded-2xl bg-slate-200/60 animate-pulse"
              />
            ))}
          </div>
        ) : brands.length > 0 ? (
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4"
          >
            {brands.sort((a, b) => a.name.localeCompare(b.name)).map((brand, index) => (
              <motion.button
                key={brand.id}
                type="button"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}
                onClick={() => handleBrandClick(brand.name)}
                className="store-interactive-card group flex flex-col items-center justify-center p-4 sm:p-5"
                title={brand.name}
              >
                <div className="w-full h-16 sm:h-20 flex items-center justify-center mb-2">
                  {(brand.logo?.url || brand.logoUrl || (typeof brand.logo === 'string' ? brand.logo : null)) ? (
                    <img
                      src={brand.logo?.url || brand.logoUrl || brand.logo}
                      alt={brand.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.brand-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        const fallback = e.target.parentElement.querySelector('.brand-fallback');
                        if (fallback) fallback.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <div
                    className="brand-fallback w-14 h-14 rounded-xl bg-slate-100 items-center justify-center group-hover:bg-primary-50 transition-colors"
                    style={{ display: (brand.logo?.url || brand.logoUrl || (typeof brand.logo === 'string' ? brand.logo : null)) ? 'none' : 'flex' }}
                  >
                    <span className="text-lg font-bold text-slate-500 group-hover:text-primary-600">
                      {brand.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-700 group-hover:text-primary-700 truncate w-full text-center px-1">
                  {brand.name}
                </span>
              </motion.button>
            ))}
          </motion.div>
        ) : (
          <div className="store-empty-state">
            <MagnifyingGlassIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No active brands available at the moment.</p>
            <button type="button" onClick={handleBrowseProducts} className="store-cta-primary mt-4">
              {browseButtonLabel}
            </button>
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="store-section pt-0 pb-16">
        <motion.div {...fadeUp} className="store-cta-banner">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          </div>
          <div className="relative z-10 max-w-xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-display font-bold">
              Ready to find the perfect gift?
            </h2>
            <p className="mt-3 text-primary-100 text-sm sm:text-base">
              Explore our full catalog or shortlist your favourites for easy comparison.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button type="button" onClick={handleBrowseProducts} className="store-cta-secondary bg-white border-white text-primary-700 hover:bg-primary-50 w-full sm:w-auto">
                {browseButtonLabel}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
              <Link to="/shortlist" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors w-full sm:w-auto justify-center">
                Go to Shortlist
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
